/* eslint-disable no-undef */
import React, {
  CSSProperties,
  ReactElement, useEffect, useRef, useState,
} from 'react';
import axios from 'axios';
import * as Papa from 'papaparse';
import moment from 'moment';
import { Button, Row, Skeleton } from 'antd';
import { KmeansAlgorithm, MarkerClusterer } from '@googlemaps/markerclusterer';
import { GeoJSONPoint, parse } from 'wellknown';
import { deselectIntersections, selectIntersections, setPmState } from '../store/entities/pm';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import Intersection from '../types/Intersection';
import { IntersectionId } from '../types/primitives';
import { getIntersectionURL } from '../util/urlApi';
import {
  BLUE_ICON_URL,
  INTERSECTION_CACHE_EXPIRATION_TIME,
  LOADING_MESSAGE,
  MIN_MAP_ZOOM_BEFORE_CLUSTER,
  ORANGE_ICON_URL,
  ORGANIZATION_STATE_MAP,
  USE_DARK_MODE,
  YELLOW_ICON_URL,
} from '../util/constants';

interface MapPageProps {
  style?: CSSProperties;
}

const MapPage = ({ style }: MapPageProps): ReactElement => {
  const dispatch = useAppDispatch();
  const selectedIntersections = useAppSelector((state) => (
    state.entities.pm.selectedIntersections));
  const availableIntersections = useAppSelector((state) => (
    state.entities.pm.availableIntersections));
  const organizationName = useAppSelector((state) => state.entities.pm.organizationName);
  const currentMouseOverIntersection = useAppSelector(
    (state) => state.entities.pm.currentMouseOverIntersection,
  );
  const selectedIntersectionOnChart = useAppSelector(
    (state) => state.entities.pm.selectedIntersectionOnChart,
  );
  const sessionId = useAppSelector((state) => state.entities.pm.sessionId);
  const mapCenter = useAppSelector((state) => state.entities.pm.mapCenter);
  const mapZoom = useAppSelector((state) => state.entities.pm.mapZoom);
  const clusterMarkers = useAppSelector((state) => state.entities.pm.clusterMarkers);

  const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
  const [mapCenterPosition, setMapCenterPosition] = useState<google.maps.LatLngLiteral>({
    lat: 39.801804,
    lng: -86.156207,
  });

  const mapRef = useRef<google.maps.Map>();
  // const drawingManagerRef = useRef<google.maps.drawing.DrawingManager>(); // Deprecated May 2026
  const drawingManagerRef = useRef<any>();
  const markerClustererRef = useRef<MarkerClusterer>();

  useEffect(() => {
    // Parse the point, destructure the GeoJSON, and set the map center.
    const point = parse(mapCenter) as GeoJSONPoint;
    const [lng, lat] = point.coordinates;

    setMapCenterPosition({
      lat,
      lng,
    });
  }, [mapCenter]);

  // Get the intersections from the backend
  useEffect(() => {
    if (sessionId) {
      axios.get(getIntersectionURL(), { headers: { Authorization: `Bearer ${sessionId}` } })
        .then((res) => {
          localStorage.setItem('intersectionCache', res.data);
          localStorage.setItem('intersectionCacheDatetime',
            moment()
              .toISOString());
          loadIntersectionsFromCSV(res.data);
        });
    }
  }, [sessionId]);

  // Update marker clusterer
  useEffect(() => {
    if (availableIntersections && availableIntersections.length) {
      if (markerClustererRef.current) markerClustererRef.current?.setMap(null);

      const markerList = generateMarkers();
      if (clusterMarkers && mapRef.current) {
        markerClustererRef.current = new MarkerClusterer({
          markers: markerList,
          map: mapRef.current,

          algorithm: new KmeansAlgorithm({
            numberOfClusters: () => (
              // If the zoom is above the minimum (too zoomed out), get rid of clusters by having
              // an infinite number of them (MAX_SAFE_INTEGER)
              (mapRef.current?.getZoom() || mapZoom) > MIN_MAP_ZOOM_BEFORE_CLUSTER
                ? Number.MAX_SAFE_INTEGER : 15),
          }),
        });
      }
      setMarkers(markerList);
    }
  }, [availableIntersections, clusterMarkers]);

  useEffect(() => {
    const selectedIntersectionId = selectedIntersectionOnChart
      ? selectedIntersectionOnChart.intersectionId : null;
    const intersectionToHighlight = selectedIntersectionId || currentMouseOverIntersection;
    markers.forEach((marker) => {
      // Compare the current state and previous state of each marker.
      // If one needs changed, then change it.
      // This prevents redrawing every marker.
      // A better way to do this could be to keep track of the previously selected
      // intersections and find the difference.
      const intersectionId: IntersectionId = Math.abs(
        marker.getZIndex() as number,
      ) as IntersectionId;
      const selected = selectedIntersections.includes(intersectionId);
      const shouldBeHighlighted = intersectionId === intersectionToHighlight;

      const desiredState = {
        opacity: getMarkerOpacity(selected),
        zIndex: getMarkerZIndex(shouldBeHighlighted, intersectionId),
        icon: getMarkerIcon(shouldBeHighlighted, intersectionId),
      };
      const curState = {
        opacity: marker.getOpacity(),
        zIndex: marker.getZIndex(),
        icon: marker.getIcon() as google.maps.Icon,
      };
      if (!(
        desiredState.opacity === curState.opacity
        && desiredState.zIndex === curState.zIndex
        && desiredState.icon.url === curState.icon.url
      )) {
        marker.setOpacity(getMarkerOpacity(selected));
        marker.setIcon(getMarkerIcon(shouldBeHighlighted, intersectionId));
        marker.setZIndex(getMarkerZIndex(shouldBeHighlighted, intersectionId));
      }
    });
  }, [selectedIntersections, currentMouseOverIntersection, selectedIntersectionOnChart]);

  // Grab the cached intersections
  useEffect(() => {
    // Find if the time between the cache's datetime is more than the expiration time
    const cacheMoment = moment(localStorage.getItem('intersectionCacheDatetime'));
    const diff = moment()
      .diff(cacheMoment);
    const isExpired = diff > INTERSECTION_CACHE_EXPIRATION_TIME.asMilliseconds();
    if (isExpired) return;

    if (localStorage.getItem('intersectionCache')) {
      try {
        loadIntersectionsFromCSV(localStorage.getItem('intersectionCache') as string);
      } catch (e) {
        localStorage.removeItem('intersectionCache');
      }
    }
    createMap();
  }, []);

  const loadIntersectionsFromCSV = (csvData: string) => {
    // Parse CSV data
    interface RawIntersection {
      'intersection_id': number,
      'intersection_name': string,
      'state': string,
      'metro_area': string,
      lat: number,
      lon: number,
    }

    const rawIntersections = Papa.parse<RawIntersection>(csvData, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: true,
    }).data;

    if (!rawIntersections || !rawIntersections.length) return;

    // Create Intersection literals from the raw intersections
    const intersectionList = rawIntersections
      .map((i) => ({
        intersectionId: i.intersection_id,
        intersectionName: i.intersection_name,
        state: i.state,
        metroArea: i.metro_area,
        position: {
          lat: i.lat,
          lng: i.lon,
        },
      }))
      .sort((a, b) => a.intersectionId - b.intersectionId);

    // Set them in the store
    dispatch(setPmState({
      availableIntersections: intersectionList,
    }));
  };

  // Update the drawing manager when the intersections change
  useEffect(() => {
    if (!mapRef.current) createMap();
    // This is here just to check that createMap() worked and to satisfy TS
    if (!mapRef.current) return;

    // if (drawingManagerRef.current) drawingManagerRef.current?.setMap(null);
    if (drawingManagerRef.current) {
      window.google.maps.event.clearInstanceListeners(drawingManagerRef.current);
      drawingManagerRef.current.setMap(null);
    }

    drawingManagerRef.current = new window.google.maps.drawing.DrawingManager({
      map: mapRef.current,
      drawingControl: true,
      drawingControlOptions: {
        position: window.google.maps.ControlPosition.RIGHT_TOP,
        drawingModes: [window.google.maps.drawing.OverlayType.POLYGON],
      },
    });

    /* eslint-disable no-underscore-dangle */
    drawingManagerRef.current._buildToolbar();
    /* eslint-enable no-underscore-dangle */

    window.google.maps.event.addListener(
      drawingManagerRef.current,
      'polygoncomplete',
      onPolygonComplete,
    );

    // drawingManagerRef.current?.addListener('polygoncomplete', onPolygonComplete);
    // drawingManagerRef.current.setMap(mapRef.current);
  }, [availableIntersections]);

  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current?.setCenter(mapCenterPosition);
    mapRef.current?.setZoom(mapZoom);
  }, [mapCenterPosition, mapZoom]);

  const createMap = () => {
    mapRef.current = new google.maps.Map(
      document.getElementById('google-map') as HTMLElement,
      {
        center: mapCenterPosition,
        zoom: mapZoom,
        gestureHandling: 'greedy',
        mapId: USE_DARK_MODE ? 'bd86bab7d6f1db7e' : '3a93fb14d8979ea3',
      },
    );
    if (drawingManagerRef.current) drawingManagerRef.current?.setMap(null);
  };

  const generateMarkers = (): google.maps.Marker[] => {
    // Clear out all the old ones
    markers.forEach((marker) => marker.setMap(null));
    let filtered = availableIntersections;
    if (organizationName) {
      filtered = availableIntersections
        .filter((i) => (
          i.state.match(ORGANIZATION_STATE_MAP[organizationName])));
    }
    if (availableIntersections && availableIntersections.length) {
      return filtered
        .map((i) => {
          const selected = selectedIntersections.includes(i.intersectionId);
          const isMousedOver = i.intersectionId === currentMouseOverIntersection;
          const marker = new google.maps.Marker({
            position: i.position,
            zIndex: getMarkerZIndex(isMousedOver, i.intersectionId),
            icon: getMarkerIcon(isMousedOver, i.intersectionId),
            opacity: getMarkerOpacity(selected),
            clickable: true,
            ...(!clusterMarkers && { map: mapRef.current }),
          });
          marker.addListener('click', () => {
            handleMarkerClick(i, isMarkerSelected(marker.getOpacity() as number));
          });
          return marker;
        });
    }
    return [];
  };

  const getMarkerIcon = (isMousedOver: boolean, id: IntersectionId) => {
    let url: string;
    if (isMousedOver) {
      url = BLUE_ICON_URL;
    } else if (id >= 10000) {
      // intersection_id >= 10000 is a roundabout, color them yellow
      url = YELLOW_ICON_URL;
    } else {
      url = ORANGE_ICON_URL;
    }
    return { url };
    // url: isMousedOver ? BLUE_ICON_URL : ORANGE_ICON_URL
    // }
  };

  const getMarkerZIndex = (isMousedOver: boolean, id: IntersectionId) => (
    id * (isMousedOver ? 1 : -1));

  const getMarkerOpacity = (selected: boolean) => (selected ? 1.0 : 0.4);

  const isMarkerSelected = (opacity: number) => opacity === 1;

  const handleMarkerClick = (intersection: Intersection, selected: boolean) => {
    if (!selected) {
      dispatch(selectIntersections([intersection.intersectionId]));
    } else {
      dispatch(deselectIntersections([intersection.intersectionId]));
    }
  };

  const unselectAllMarkers = () => {
    dispatch(deselectIntersections(selectedIntersections));
  };

  const onPolygonComplete = (polygon: google.maps.Polygon) => {
    if (!availableIntersections) return;
    const updateList = availableIntersections.reduce((acc: IntersectionId[], intersection) => {
      const isWithinRect = window.google.maps.geometry.poly.containsLocation(
        new window.google.maps.LatLng(intersection.position),
        polygon,
      );
      if (isWithinRect) acc.push(intersection.intersectionId);
      return acc;
    }, []);
    dispatch(selectIntersections(updateList));
    polygon.setMap(null);
  };

  return (
    <>
      <div style={{
        flexDirection: 'column',
        height: '100%',
        display: availableIntersections.length ? 'flex' : 'none',
      }}
      >
        <Row style={{
          justifyContent: 'space-between',
          float: 'right',
          paddingBottom: '1em',
        }}
        >
          <Button onClick={unselectAllMarkers} danger>Unselect All</Button>
        </Row>
        <div
          id="google-map"
          style={{
            height: 'calc(100% - 5em)',
            width: '100%',
            alignSelf: 'flex-start',
            ...style,
          }}
        />
      </div>
      <div style={{
        display: availableIntersections.length ? 'none' : 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
      >
        <Skeleton />
        {/* <div>Loading...</div> */}
        <br />
        {/* eslint-disable-next-line react/no-danger */}
        <div dangerouslySetInnerHTML={{ __html: LOADING_MESSAGE }} />
        <div />
      </div>
    </>
  );
};
export default MapPage;
